import { lazy } from 'react';
import { CRYR_ADMIN_NAV_ROLES } from '../../auth/roles';

const HomePage = lazy(() => import("../../pages/Home"));
const ResidentDashboardPage = lazy(() => import("../../pages/ResidentDashboard"));

export const UpgradeNav = [
    {
        icon: 'pe-7s-diamond',
        label: 'Upgrade to PRO',
        to: 'https://dashboardpack.com/theme-details/architectui-dashboard-react-pro?utm_source=free_theme&utm_medium=sidebar&utm_campaign=upgrade_cta&utm_content=menu_item',
        external: true,
        allowedRoles: ["Super Admin"],
    },
];

// Main navigation items - single source of truth for both navigation and route permissions
export const MainNav = [
    {
        icon: 'pe-7s-home',
        label: 'Dashboard',
        to: '/home',
        component: HomePage,
        allowedRoles: [...CRYR_ADMIN_NAV_ROLES, 'Business User', 'Customer User'],
        rolesToComponents: {
            "Super Admin": HomePage,
            "System Admin": HomePage,
            "Business Admin": HomePage,
            "CRYR Admin": HomePage,
            "Business User": HomePage,
            "Customer User": ResidentDashboardPage,
        },
    },
    {
        icon: 'pe-7s-portfolio',
        label: 'Business Model',
        to: '/business-models',
        component: lazy(() => import("../../pages/BusinessModelManagement")),
        allowedRoles: CRYR_ADMIN_NAV_ROLES,
    },
    {
        icon: 'pe-7s-network',
        label: 'PMS',
        to: '/pms',
        component: lazy(() => import("../../pages/PmsManagement")),
        allowedRoles: CRYR_ADMIN_NAV_ROLES,
    },
    {
        icon: 'pe-7s-id',
        label: 'Management Company',
        to: '/management-companies',
        component: lazy(() => import("../../pages/ManagementCompanyManagement")),
        allowedRoles: CRYR_ADMIN_NAV_ROLES,
    },
    
    {
        icon: 'pe-7s-home',
        label: 'Property',
        to: '/properties',
        component: lazy(() => import("../../pages/PropertyManagement")),
        allowedRoles: CRYR_ADMIN_NAV_ROLES,
    },
    {
        icon: 'pe-7s-users',
        label: 'User Management',
        to: '/users',
        component: lazy(() => import("../../pages/UserManagement")),
        allowedRoles: CRYR_ADMIN_NAV_ROLES,
    },
    {
        icon: 'pe-7s-gift',
        label: 'Campaigns',
        to: '/campaigns',
        component: lazy(() => import("../../pages/CampaignManagement")),
        allowedRoles: CRYR_ADMIN_NAV_ROLES,
    },
    {
        icon: 'pe-7s-ticket',
        label: 'Voucher Management',
        to: '/vouchers',
        component: lazy(() => import("../../pages/VoucherManagement")),
        allowedRoles: CRYR_ADMIN_NAV_ROLES,
    },
    {
        icon: 'pe-7s-credit',
        label: 'Stripe Management',
        to: '/stripe',
        component: lazy(() => import("../../pages/StripeManagement")),
        allowedRoles: CRYR_ADMIN_NAV_ROLES,
    },
    {
        icon: 'pe-7s-piggy',
        label: 'Plaid Management',
        to: '/plaid',
        component: lazy(() => import("../../pages/PlaidManagement")),
        allowedRoles: CRYR_ADMIN_NAV_ROLES,
    },
    {
        // B2C Main Menu
        icon: 'pe-7s-users',
        label: 'B2C',
        allowedRoles: CRYR_ADMIN_NAV_ROLES,
        content: [
            {
                label: 'OTP Data',
                to: '/b2c/otp-data',
                component: lazy(() => import("../../pages/OTPData")),
                allowedRoles: CRYR_ADMIN_NAV_ROLES,
            },
        ],
    },
    {
        icon: 'pe-7s-news-paper',
        label: 'Logs',
        to: '/logs/view',
        component: lazy(() => import("../../pages/LogsView")),
        allowedRoles: CRYR_ADMIN_NAV_ROLES,
    },
    {
        icon: 'pe-7s-bell',
        label: 'Notifications',
        to: '/notifications',
        component: lazy(() => import("../../pages/NotificationsManagement")),
        allowedRoles: CRYR_ADMIN_NAV_ROLES,
    },
        
    {
        icon: 'pe-7s-tools',
        label: 'Utils',
        to: '/utils',
        component: lazy(() => import("../../pages/Utils")),
        allowedRoles: ["Super Admin"],
    },
    {
        icon: 'pe-7s-plugin',
        label: 'Get Transaction Categories',
        to: '/resman/get-transaction-categories',
        component: lazy(() => import("../../pages/ResmanGetTransactionCategories")),
        allowedRoles: CRYR_ADMIN_NAV_ROLES,
    },
    {
        // Main Menu and Sub Menu setup
        icon: 'pe-7s-mail',
        label: 'Emailing',
        allowedRoles: ["Super Admin"],
        content: [
            {
                label: 'Emailing Wizard',
                to: '/emailing/wizard',
                component: lazy(() => import("../../pages/EmailingWizard")),
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Emailing Jobs',
                to: '/emailing/jobs',
                component: lazy(() => import("../../pages/EmailingJobs")),
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Resident Bills Email',
                to: '/emailing/resident-bills',
                component: lazy(() => import("../../pages/ResidentBillsEmail")),
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Resident Bills Email V2',
                to: '/emailing/resident-bills-v2',
                component: lazy(() => import("../../pages/ResidentBillsEmailV2")),
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Resident Emails V3',
                to: '/emailing/resident-emails-v3',
                component: lazy(() => import("../../pages/ResidentEmailsV3")),
                allowedRoles: ["Super Admin"],
            },
        ],
    },
    {
        icon: 'pe-7s-hammer',
                label: 'Job User Update',
                to: '/susi/updates',
        component: lazy(() => import("../../pages/SusiUpdates")),
        allowedRoles: ["Super Admin"],
    },
    {
        icon: 'pe-7s-rocket',
        label: 'Admin Dashboard',
        to: '/dashboards/crm',
        component: lazy(() => import("../../DemoPages/Dashboards")),
        allowedRoles: ["Super Admin"],
    },
    {
        icon: 'pe-7s-display2',
        label: 'Components',
        to: '/components',
        component: lazy(() => import("../../DemoPages/Components")),
        allowedRoles: ["Super Admin"],
    },
    {
        icon: 'pe-7s-display2',
        label: 'Forms',
        to: '/forms',
        component: lazy(() => import("../../DemoPages/Forms")),
        allowedRoles: ["Super Admin"],
    },
    {
        icon: 'pe-7s-display2',
        label: 'Charts',
        to: '/charts',
        component: lazy(() => import("../../DemoPages/Charts")),
        allowedRoles: ["Super Admin"],
    },
    {
        icon: 'pe-7s-display2',
        label: 'Tables',
        to: '/tables',
        component: lazy(() => import("../../DemoPages/Tables")),
        allowedRoles: ["Super Admin"],
    },
    {
        icon: 'pe-7s-display2',
        label: 'Elements',
        to: '/elements',
        component: lazy(() => import("../../DemoPages/Elements")),
        allowedRoles: ["Super Admin"],
    },
    {
        icon: 'pe-7s-display2',
        label: 'Widgets',
        to: '/widgets',
        component: lazy(() => import("../../DemoPages/Widgets")),
        allowedRoles: ["Super Admin"],
    },
    {
        icon: 'pe-7s-display2',
        label: 'User Pages',
        to: '/pages',
        component: lazy(() => import("../../DemoPages/UserPages")),
        allowedRoles: ["Super Admin"],
    },
    {
        icon: 'pe-7s-display2',
        label: 'Applications',
        to: '/apps',
        component: lazy(() => import("../../DemoPages/Applications")),
        allowedRoles: ["Super Admin"],
    },
    {
        icon: 'pe-7s-display2',
                label: 'Dashboards',
                to: '/dashboards',
                component: lazy(() => import("../../DemoPages/Dashboards")),
        allowedRoles: ["Super Admin"],
    },
            
    
];
export const ComponentsNav = [
    {
        icon: 'pe-7s-diamond',
        label: 'Elements',
        allowedRoles: ["Super Admin"],
        content: [
            {
                label: 'Standard Buttons',
                to: '/elements/buttons-standard',
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Dropdowns',
                to: '/elements/dropdowns',
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Icons',
                to: '/elements/icons',
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Badges',
                to: '/elements/badges-labels',
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Cards',
                to: '/elements/cards',
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'List Groups',
                to: '/elements/list-group',
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Navigation Menus',
                to: '/elements/navigation',
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Utilities',
                to: '/elements/utilities',
                allowedRoles: ["Super Admin"],
            },
        ],
    },
    {
        icon: 'pe-7s-car',
        label: 'Components',
        allowedRoles: ["Super Admin"],
        content: [
            {
                label: 'Tabs',
                to: '/components/tabs',
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Notifications',
                to: '/components/notifications',
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Modals',
                to: '/components/modals',
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Progress Bar',
                to: '/components/progress-bar',
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Tooltips & Popovers',
                to: '/components/tooltips-popovers',
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Carousel',
                to: '/components/carousel',
                allowedRoles: ["Super Admin"],
            },
            {
                label: 'Maps',
                to: '/components/maps',
                allowedRoles: ["Super Admin"],
            },
        ],
    },
    {
        icon: 'pe-7s-display2',
        label: 'Regular Tables',
        to: '/tables/regular-tables',
        allowedRoles: ["Super Admin"],
    },
];
export const FormsNav = [
    {
        icon: 'pe-7s-light',
        label: 'Controls',
        to: '/forms/controls',
        component: lazy(() => import("../../DemoPages/Forms")),
        allowedRoles: ["Super Admin"],
    },
    {
        icon: 'pe-7s-eyedropper',
        label: 'Layouts',
        to: '/forms/layouts',
        component: lazy(() => import("../../DemoPages/Forms")),
        allowedRoles: ["Super Admin"],
    },
    {
        icon: 'pe-7s-pendrive',
        label: 'Validation',
        to: '/forms/validation',
        component: lazy(() => import("../../DemoPages/Forms")),
        allowedRoles: ["Super Admin"],
    },
];
export const WidgetsNav = [
    {
        icon: 'pe-7s-graph2',
        label: 'Dashboard Boxes',
        to: '/widgets/chart-boxes-3',
        allowedRoles: ["Super Admin"],
    },
];
export const ChartsNav = [
    {
        icon: 'pe-7s-graph2',
        label: 'ChartJS',
        to: '/charts/chartjs',
        allowedRoles: ["Super Admin"],
    },
];